import { z } from 'zod';
import { plannerFirstReadSchema } from './planner-first-reads.js';
import { actionDraftRevisionSnapshotSchema } from '../action-draft.contract.js';
import { AI_CHAT_AMBIGUITY_CANDIDATE_LIMIT } from '@govard/ai-chat-kit';
import {
  savedCompoundCandidateChoice,
  compoundCandidateOrdinal,
} from './compound-selection.contract.js';
import type { IntentEnvelopeV1 } from './intent-envelope.js';
const id = z.string().min(1).max(200);
export const plannerReadSelectionSchema = z
  .object({
    type: z.literal('planner-first-read.selection.v1'),
    entity: z.enum(['contact', 'company', 'deal']),
    candidates: z
      .array(
        z
          .object({
            entityType: z.enum(['contact', 'company', 'deal']),
            entityId: id,
            label: z.string().min(1).max(300),
            version: z.string().datetime({ offset: true }),
            matchedFields: z.array(z.string()).max(20),
          })
          .strict(),
      )
      .min(1)
      .max(AI_CHAT_AMBIGUITY_CANDIDATE_LIMIT),
    read: plannerFirstReadSchema.optional(),
    narrow: z.literal(true).optional(),
    consumed: z.boolean(),
    consumedByMessageId: id.optional(),
    sourceHash: actionDraftRevisionSnapshotSchema.shape.contentHash,
  })
  .strict()
  .refine(
    (s) =>
      s.candidates.every((c) => c.entityType === s.entity) &&
      new Set(s.candidates.map((c) => c.entityId)).size === s.candidates.length,
  );
export type PlannerReadSelection = z.infer<typeof plannerReadSelectionSchema>;
export function isPlannerReadSelectionEnvelope(
  envelope: IntentEnvelopeV1,
  selection: PlannerReadSelection,
): boolean {
  const op = envelope.operations[0];
  return (
    envelope.mode === 'crm_read' &&
    envelope.operations.length === 1 &&
    envelope.missingInformation.length === 0 &&
    envelope.unsupportedRequirements.length === 0 &&
    !!op &&
    (op.entity === selection.entity ||
      (selection.read?.capability === 'documents' &&
        op.entity === 'document' &&
        ((selection.entity === 'company' && selection.read.company !== undefined) ||
          (selection.entity === 'deal' && selection.read.deal !== undefined)))) &&
    ['read', 'search'].includes(op.operation) &&
    op.fields.length === 0 &&
    op.relations.length === 0 &&
    op.dependsOn.length === 0
  );
}
const READ_CONFIRM_REPLIES = new Set([
  'да',
  'ага',
  'верно',
  'подтверждаю',
  'она',
  'это она',
  'yes',
  'yeah',
  'yep',
  'correct',
  'confirm',
  'it is',
]);
/** A one-item checkpoint is accepted by a single confirmation word. */
function readConfirmationOrdinal(text: string, labels: readonly string[]): number | null {
  if (labels.length !== 1) return null;
  return READ_CONFIRM_REPLIES.has(text.trim().toLocaleLowerCase()) ? 1 : null;
}
export function isReadCandidateReply(text: string, labels: readonly string[]): boolean {
  return (
    readConfirmationOrdinal(text, labels) !== null ||
    savedCompoundCandidateChoice(text, labels) !== null ||
    [
      'первого',
      'второго',
      'третьего',
      'четвертого',
      'четвёртого',
      'пятого',
      '3rd',
      '4th',
      '5th',
    ].includes(text.trim().toLocaleLowerCase()) ||
    /^\d+$/u.test(text.trim())
  );
}
/** Read-only aliases are scoped to PF; existing write/legacy ordinal admission is unchanged. */
export function savedReadCandidateOrdinal(text: string, labels: readonly string[]): number | null {
  const choice = savedCompoundCandidateChoice(text, labels);
  if (choice) return compoundCandidateOrdinal(choice, labels);
  const confirmation = readConfirmationOrdinal(text, labels);
  if (confirmation !== null) return confirmation;
  const ordinal = new Map([
    ['первого', 1],
    ['второго', 2],
    ['третьего', 3],
    ['четвертого', 4],
    ['четвёртого', 4],
    ['пятого', 5],
    ['3rd', 3],
    ['4th', 4],
    ['5th', 5],
  ]).get(text.trim().toLocaleLowerCase());
  return ordinal !== undefined && ordinal <= labels.length ? ordinal : null;
}
